/**
 * Componente para valorar la utilidad de una capacitación
 * Escala deslizante de 1 a 10 con ícono de bus
 */
import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner, Alert } from 'react-bootstrap';
import { FaBus, FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';
import './ValoracionCapacitacion.css';

const API_URL = (process.env.REACT_APP_API_URL || "/").replace(/\/$/, '');

const ValoracionCapacitacion = ({ moduloId, legajo, onValoracionGuardada }) => {
  const [valoracion, setValoracion] = useState(5);
  const [valoracionExistente, setValoracionExistente] = useState(null);
  const [comentario, setComentario] = useState('');
  const [loading, setLoading] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [error, setError] = useState(null);

  // Debug: Verificar props recibidas
  useEffect(() => {
    console.log('📝 ValoracionCapacitacion - Props:', { moduloId, legajo });
  }, [moduloId, legajo]);

  // Cargar valoración existente al montar el componente
  useEffect(() => {
    cargarValoracionExistente();
  }, [moduloId, legajo]);

  const cargarValoracionExistente = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_URL}/api/capacitaciones/valoracion/${moduloId}/${legajo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success && res.data.data) {
        setValoracionExistente(res.data.data);
        setValoracion(res.data.data.valoracion);
        setComentario(res.data.data.comentario || '');
        setGuardado(true);
      }
    } catch (err) {
      console.error('Error al cargar valoración existente:', err);
    }
  };

  const handleValoracionChange = (e) => {
    setValoracion(parseInt(e.target.value));
    setGuardado(false);
  };

  const handleGuardarValoracion = async () => {
    setLoading(true);
    setError(null);

    // Validación previa
    if (!legajo) {
      setError('No se pudo obtener tu número de legajo. Recargá la página.');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Guardar valoración
      const res = await axios.post(
        `${API_URL}/api/capacitaciones/valoracion`,
        {
          modulo_id: moduloId,
          legajo: legajo,
          valoracion: valoracion,
          comentario: comentario.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        setGuardado(true);
        
        // Marcar módulo como visto
        try {
          console.log('🔄 Intentando marcar módulo como visto...', { moduloId, legajo });
          const vistoRes = await axios.post(
            `${API_URL}/api/capacitaciones/marcar-visto`,
            {
              modulo_id: moduloId,
              legajo: legajo
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('✅ Módulo marcado como visto:', vistoRes.data);
        } catch (vistoErr) {
          console.error('❌ ERROR al marcar como visto:', {
            message: vistoErr.message,
            response: vistoErr.response?.data,
            status: vistoErr.response?.status
          });
        }
        
        if (onValoracionGuardada) {
          onValoracionGuardada(valoracion);
        }
      }
    } catch (err) {
      // Mostrar mensaje específico del servidor si está disponible
      const errorMsg = err.response?.data?.error || err.response?.data?.mensaje || 'Error al guardar la valoración. Intentá de nuevo.';
      setError(errorMsg);
      console.error('❌ Error al guardar valoración:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Obtener descripción según el valor
  const getDescripcionByValue = (value) => {
    if (value <= 3) return 'Poco útil';
    if (value <= 6) return 'Útil';
    if (value <= 8) return 'Muy útil';
    return '¡Excelente!';
  };

  return (
    <Card className="valoracion-capacitacion-card">
      <Card.Body className="p-4">
        <div className="valoracion-header text-center">
          <h6>¿Qué te pareció esta capacitación?</h6>
          <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
            Tu opinión nos ayuda a mejorar el contenido para todos.
          </p>
          
          {guardado && (
            <Alert variant="success" className="mb-3 py-2">
              <FaCheckCircle className="me-2" />
              {valoracionExistente ? 'Valoración actualizada' : 'Valoración guardada'}
            </Alert>
          )}

          {error && (
            <Alert variant="danger" className="mb-3 py-2">
              {error}
            </Alert>
          )}
        </div>

        {/* Slider */}
        <div className="slider-container">
          <div className="slider-wrapper">
            {/* Ícono de bus que sigue al thumb */}
            <div 
              className="bus-icon-thumb"
              style={{
                left: `calc(${(valoracion - 1) * 11.11}% - 25px)`
              }}
            >
              <FaBus style={{ color: '#e91e63', fontSize: '2.8rem' }} />
            </div>
            
            <input
              type="range"
              min="1"
              max="10"
              value={valoracion}
              onChange={handleValoracionChange}
              className="valoracion-slider"
              style={{
                background: `linear-gradient(to right, #e91e63 0%, #e91e63 ${(valoracion - 1) * 11.11}%, #e9ecef ${(valoracion - 1) * 11.11}%, #e9ecef 100%)`
              }}
              disabled={loading}
            />
          </div>
          
          <div className="slider-labels">
            <span className="label-start">Poco útil</span>
            <span className="label-end">Muy útil</span>
          </div>
        </div>

        {/* Valor seleccionado */}
        <div className="valor-seleccionado text-center">
          <div className="valor-numero">
            {valoracion}
          </div>
          <div className="valor-descripcion">
            {getDescripcionByValue(valoracion)}
          </div>
        </div>

        {/* Comentario opcional */}
        <div className="comentario-section">
          <label className="form-label">
            💬 Comentario adicional (opcional)
          </label>
          <textarea
            className="form-control"
            rows="3"
            value={comentario}
            onChange={(e) => {
              setComentario(e.target.value);
              setGuardado(false);
            }}
            placeholder="Comparte tu experiencia sobre esta capacitación..."
            disabled={loading}
          />
        </div>

        {/* Botón de guardar */}
        <div className="text-center mt-4">
          <Button
            variant="primary"
            size="lg"
            onClick={handleGuardarValoracion}
            disabled={loading || guardado}
            className="px-5"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Guardando...
              </>
            ) : guardado ? (
              <>
                <FaCheckCircle className="me-2" />
                {valoracionExistente ? 'Actualizar valoración' : 'Valorado'}
              </>
            ) : (
              'Enviar valoración'
            )}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ValoracionCapacitacion;
